import React from "react";
import { Box, Chip, Tooltip } from "@mui/material";
import { ResourceLabelContentArg } from "@fullcalendar/resource";
import UserPopover from "./UserPopover";
import WarningBadge from "./WarningBadge";

interface ResourceHeaderWithDeltaProps {
  resource: ResourceLabelContentArg;
  onConfirm: () => void;
  onRouteplanUserChange?: any;
  onResourceClick?: (resourceId: string) => void;
}

/**
 * Resource header component that displays user name with delta minutes indicator.
 * Always shows both day and year deltas stacked (when non-zero).
 * Tooltip on hover labels which is which.
 */
const ResourceHeaderWithDelta: React.FC<ResourceHeaderWithDeltaProps> = ({
  resource,
  onConfirm,
  onRouteplanUserChange,
  onResourceClick,
}) => {
  const deltaMinutes = resource.resource.extendedProps?.deltaMinutes;
  const yearDeltaMinutes = resource.resource.extendedProps?.yearDeltaMinutes;
  const warningCount = resource.resource.extendedProps?.warningCount;
  const hasWarnings = warningCount !== undefined && warningCount > 0;

  const hasDayDelta = deltaMinutes !== undefined && deltaMinutes !== null && deltaMinutes !== 0;
  const hasYearDelta = yearDeltaMinutes !== undefined && yearDeltaMinutes !== null && yearDeltaMinutes !== 0;

  const formatDelta = (minutes: number): string => {
    const abs = Math.abs(minutes);
    const h = Math.floor(abs / 60);
    const m = abs % 60;
    const sign = minutes > 0 ? '+' : '-';
    if (h > 0 && m > 0) return `${sign}${h}h${m}m`;
    if (h > 0) return `${sign}${h}h`;
    return `${sign}${m}m`;
  };

  const colorFor = (minutes: number) => (minutes > 0 ? '#2e7d32' : '#c62828');

  const showBadge = hasDayDelta || hasYearDelta;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.5,
        width: "100%",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {onResourceClick ? (
        <>
          <span
            style={{ cursor: "pointer", textDecoration: "underline" }}
            onClick={() => onResourceClick(resource.resource.id)}
          >
            {resource.resource.title}
          </span>
          {hasWarnings && <WarningBadge count={warningCount!} />}
        </>
      ) : (
        <UserPopover
          resource={resource}
          onConfirm={onConfirm}
          onRouteplanUserChange={onRouteplanUserChange}
        />
      )}
      {showBadge && (
        <Tooltip
          title={
            <Box sx={{ fontSize: '12px', minWidth: 90 }}>
              {hasDayDelta && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                  <span>Tag:</span>
                  <span style={{ color: colorFor(deltaMinutes!), fontWeight: 600 }}>{formatDelta(deltaMinutes!)}</span>
                </Box>
              )}
              {hasYearDelta && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                  <span>Jahr:</span>
                  <span style={{ color: colorFor(yearDeltaMinutes!), fontWeight: 600 }}>{formatDelta(yearDeltaMinutes!)}</span>
                </Box>
              )}
            </Box>
          }
          placement="bottom"
          arrow
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              flexShrink: 0,
              cursor: 'default',
              userSelect: 'none',
              lineHeight: 1.2,
            }}
          >
            {hasDayDelta && (
              <span style={{ fontSize: '10px', fontWeight: 700, color: colorFor(deltaMinutes!), whiteSpace: 'nowrap' }}>
                {formatDelta(deltaMinutes!)}
              </span>
            )}
            {hasYearDelta && (
              <span style={{ fontSize: '10px', fontWeight: 700, color: colorFor(yearDeltaMinutes!), whiteSpace: 'nowrap' }}>
                {formatDelta(yearDeltaMinutes!)}
              </span>
            )}
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

export default ResourceHeaderWithDelta;
